import { baseApi } from '@alltrails/redux-helpers/baseApi';
import type { Map } from '@alltrails/types/Map';
import type Trail from '@alltrails/types/Trail';
import { LngLat } from '../types/Geo';
import TrailSurfaceTypesData from '../types/TrailSurfaceTypesData';
import TrailSurfaceTypesParams from '../types/TrailSurfaceTypesParams';
import getCoordinatesFromFeatures from '../utils/getCoordinatesFromFeatures';
import { atMapsToGeojson } from '../utils/legacyGeoJSONConversions';

type TrailPolylineResponse = { trails: Trail[] };
type MapPolylineResponse = { maps: Map[] };

type GetPolylineParams = {
  baseUrl?: string;
  trailId?: string | number;
  mapId?: string | number;
};

export const mapsApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getPolyline: builder.query<LngLat[], GetPolylineParams>({
      query: ({ baseUrl = '', trailId, mapId }) => {
        if (!trailId && !mapId) {
          throw new Error('Either trailId or mapId must be provided');
        }

        if (trailId) {
          const url = `${baseUrl}/api/alltrails/trails/${trailId}`;
          const method = 'get';
          return { url, method, config: { params: { detail: 'offline', include_pending: true } } };
        }

        const url = `${baseUrl}/api/alltrails/maps/${mapId}`;
        const method = 'get';
        return { url, method, config: { params: { detail: 'deep' } } };
      },
      transformResponse: (response: TrailPolylineResponse | MapPolylineResponse, _, arg) => {
        const map = arg.trailId ? (response as TrailPolylineResponse).trails[0].defaultMap : (response as MapPolylineResponse).maps[0];
        const geoJson = atMapsToGeojson([map]);
        const newRouteCoordinates = getCoordinatesFromFeatures(geoJson) || [];
        return response ? newRouteCoordinates : [];
      }
    }),
    getTrailSurfaceTypes: builder.query<TrailSurfaceTypesData, TrailSurfaceTypesParams>({
      query: ({ includeSegments, showHidden, trailId }) => {
        return { url: `/api/alltrails/trails/${trailId}/surface_types`, config: { params: { includeSegments, showHidden } } };
      }
    }),
    /** Shallow trail payload from `GET /api/alltrails/trails/:id` (numeric id = `trail_id`). */
    getTrailShallow: builder.query<Trail, { trailId: number }>({
      query: ({ trailId }) => ({
        url: `/api/alltrails/trails/${trailId}`,
        method: 'get',
        config: {
          params: {
            detail: 'shallow',
            include_pending: 'true'
          }
        }
      }),
      transformResponse: (response: TrailPolylineResponse) => {
        const trail = response.trails?.[0];
        if (!trail) {
          throw new Error('Trail not found');
        }
        return trail;
      }
    })
  })
});

export const { useGetPolylineQuery, useGetTrailShallowQuery, useGetTrailSurfaceTypesQuery } = mapsApi;
